/**
 * Educational evidence-card composer — ADR-0122 §3.1, Slice 2, EXTENDED
 * by ADR-0123 §3.3 (10 new card kinds + the always-a-card generic
 * fallback).
 *
 * SECURITY-SENSITIVE. Read ADR-0122 §4 ("Security must-holds") AND
 * ADR-0123 §4 ("Security must-holds — carried forward") before
 * touching this file. The composer's job is to turn the categorical
 * `fail_reason` Slice 1 lands on `ShadowDecision`
 * (e.g. `secret_in_sku_aws_access_key`, `over_destructive_ceiling`,
 * `manifest_absent`) plus the already-fetched `AuditIntentSummary`
 * plus the rule's configured params into a small, RENDER-READY
 * evidence card that answers the operator's "what tripped this rule
 * in THIS mandate" question — without ever surfacing the
 * attacker-controllable raw bytes that fired the detector.
 *
 * ADR-0123 GUARANTEE — `buildEvidenceCard` is TOTAL.
 *   It NEVER returns `null`. Any row whose `fail_reason` is null /
 *   unmapped / outside the per-kind builders falls through to the
 *   GENERIC card carrying the categorical `whyThisRule` sentence for
 *   the rule (and, for the indeterminate r03 / r04 / r13 / r17 set,
 *   the verbatim `INDETERMINATE_SENTENCE`). The drawer always shows
 *   a reason card.
 *
 * Why this module is pure / why it lives next to `histogram.ts`:
 *   - kept regex-free at runtime (the categorical signal is the
 *     `fail_reason` from the edge producer; no client-side re-detection
 *     of the predicate per ADR-0122 §4 must-hold 7 + ADR-0123 §4
 *     must-hold 4);
 *   - returns a plain data record (`EvidenceCard`) the renderer in
 *     `Guardrails.tsx` plugs into the drawer above `MandateDetail`;
 *   - all masking / escaping / capping happens HERE, at composition
 *     time, so the React component receives a value
 *     already-safe-for-display and the raw secret / raw invisible
 *     codepoint / raw matched bytes NEVER reach a text node
 *     (ADR-0122 §4 must-holds 1 + 3 + 4 + 5; ADR-0123 §4 must-holds
 *     2 + 6 + 7).
 *
 * NO `dangerouslySetInnerHTML`, NO markdown parser, NO LLM call — those
 * three are forbidden in this file AND in any renderer that consumes
 * its output (CLAUDE.md §7 + ADR-0120 §4 must-hold 1 + ADR-0122 §4
 * must-hold 1 + ADR-0123 §4 must-hold 2).
 */

import type { AuditIntentSummary } from '@sill/types';
import type { Rule, RuleId } from '@sill/types';

import { whyThisRule, INDETERMINATE_SENTENCE } from './why-this-rule.js';
import { isIndeterminateShadowRule } from './shadow-indeterminate.js';

// Re-export so callers (tests, drawer integration) can reference the
// canonical indeterminate sentence without a second import path. Per
// ADR-0123 §3.3 the byte-for-byte identity with `why-this-rule.ts:48`
// is the load-bearing honesty invariant — the re-export here is a
// pass-through, never a forked copy.
export { INDETERMINATE_SENTENCE } from './why-this-rule.js';

/**
 * Which categorical "field" the evidence card describes. These are the
 * only field names the edge producer ever ships in a `fail_reason`
 * suffix (the policy handlers iterate the literal set
 * `['action','merchant','sku'] as const` for r10/r20/r22/r08 — see
 * `packages/policy/src/rules/r{08,10,20,22}_*.ts`).
 *
 * ADR-0123 widens the field set to also include the categorical /
 * comparator-shaped slots used by the new rules (r02 / r07 / r09 /
 * r14 / r15 / r18 / r19 / r25 / r_custom). None of these field labels
 * are attacker-controllable strings — they are static identifiers
 * pinned in this file.
 */
export type EvidenceCardField =
  | 'action'
  | 'merchant'
  | 'sku'
  | 'expires_at'
  | 'max_amount'
  | 'principal'
  | 'currency'
  | 'quoted_total'
  | 'signed_body'
  | 'skill_manifest'
  | 'predicate'
  | 'rule';

/**
 * One render-ready evidence card. Every string on this record is safe
 * to drop into a React text node verbatim — masking / escaping has
 * already happened in the composer.
 */
export type EvidenceCard =
  | EvidenceCardCredential
  | EvidenceCardUnicode
  | EvidenceCardInjection
  | EvidenceCardValidity
  | EvidenceCardAmount
  | EvidenceCardCurrencyUncapped
  // ADR-0123 §3.3 new card kinds.
  | EvidenceCardR02Principal
  | EvidenceCardR07DestructiveAmount
  | EvidenceCardR07DestructiveCurrencyUncapped
  | EvidenceCardR08Urgency
  | EvidenceCardR09DripPricing
  | EvidenceCardR14CartOverIntent
  | EvidenceCardR15CurrencyMismatch
  | EvidenceCardR18Manifest
  | EvidenceCardR19Subscription
  | EvidenceCardR25BodyBytes
  | EvidenceCardRCustomPredicate
  // ADR-0123 §3.3 — always-a-card generic fallback.
  | EvidenceCardGeneric;

export interface EvidenceCardCredential {
  readonly kind: 'r22_credential';
  readonly field: 'action' | 'merchant' | 'sku';
  /** Human-readable pattern class (e.g. "AWS access-key shape"). NEVER
   *  the raw matched secret, NEVER attacker-controllable. */
  readonly pattern_class: string;
  /** Stable internal pattern id (`aws_access_key` etc.) — useful for
   *  tests + stable test IDs; NEVER rendered as the explanatory weight. */
  readonly pattern_id: string;
  /** Masked display, already safe to render. Shape:
   *  `{PREFIX}████…` where `{PREFIX}` is a fixed-by-pattern-class safe
   *  prefix of at most 4 chars (e.g. `AKIA`) and `████…` is a fixed
   *  visual mask. The raw secret itself is NEVER on this string. */
  readonly masked_display: string;
}

export interface EvidenceCardUnicode {
  readonly kind: 'r20_unicode';
  readonly field: 'action' | 'merchant' | 'sku';
  /** The field value with every invisible Unicode codepoint replaced
   *  by a visible sentinel (`␛` followed by `⟨U+E00xx⟩`). The raw
   *  invisible codepoint NEVER appears on this string. */
  readonly visualized: string;
  /** Count of invisible codepoints in the source value (≥ 1 when this
   *  card is emitted — the rule does not fire otherwise). */
  readonly invisible_count: number;
  /** Per-codepoint breakdown: position + the `U+E00xx` annotation.
   *  Surface ed in a small table next to the visualized value. */
  readonly codepoints: ReadonlyArray<{
    readonly index: number;
    readonly codepoint: string;
  }>;
}

export interface EvidenceCardInjection {
  readonly kind: 'r10_injection';
  readonly field: 'action' | 'merchant' | 'sku';
  /** Human-readable marker class (e.g. "model-control marker"). NEVER
   *  the matched bytes. */
  readonly pattern_class: string;
  /** Stable internal pattern id (`model_token_marker` etc.). */
  readonly pattern_id: string;
}

export interface EvidenceCardValidity {
  readonly kind: 'r23_validity';
  readonly field: 'expires_at';
  /** Comparator: "{requested}s requested · {cap}s cap". Both numbers
   *  are formatted at composition time. `requested_seconds` is absent
   *  when the audit payload does not carry the canonical issued_at /
   *  expires_at (e.g. older record); the comparator still renders the
   *  cap with a "—" left side. */
  readonly comparator: { left: string; right: string };
}

export interface EvidenceCardAmount {
  readonly kind: 'r05_amount';
  readonly field: 'max_amount';
  /** Comparator: "{amount} {currency} · cap {cap} {currency}". Numbers
   *  are toLocaleString-formatted; `currency` is the ISO-4217 string
   *  the merchant configured — never an attacker-controllable label. */
  readonly comparator: { left: string; right: string };
}

export interface EvidenceCardCurrencyUncapped {
  readonly kind: 'r05_currency_uncapped';
  readonly field: 'max_amount';
  /** Categorical sentence: "no cap configured for {currency}". The
   *  currency is the ISO-4217 string from intent_summary. */
  readonly headline: string;
}

/* ------------------------------------------------------------------ */
/* ADR-0123 §3.3 — new card kinds.                                    */
/* ------------------------------------------------------------------ */

/**
 * r02 — IntentMandate missing / principal not user-delegated.
 * Static categorical sentence; NEVER reads the mandate.
 */
export interface EvidenceCardR02Principal {
  readonly kind: 'r02_principal_category';
  readonly field: 'principal';
  readonly headline: string;
}

/**
 * r07 — destructive action above auto-approve ceiling. Comparator of
 * `intent.max_amount {currency}` vs `cap {ceiling} {currency}`. Both
 * sides are numeric + ISO-4217 — never attacker-controllable narrative.
 */
export interface EvidenceCardR07DestructiveAmount {
  readonly kind: 'r07_destructive_amount';
  readonly field: 'max_amount';
  readonly comparator: { left: string; right: string };
}

/**
 * r07 — destructive action in a currency the merchant has not
 * configured (or `destructive_non_usd_unevaluable` legacy path).
 * Static headline; `legacy: true` for the legacy-USD-only annotation.
 */
export interface EvidenceCardR07DestructiveCurrencyUncapped {
  readonly kind: 'r07_destructive_currency_uncapped';
  readonly field: 'max_amount';
  readonly headline: string;
  readonly legacy: boolean;
}

/**
 * r08 — urgency phrasing detected. Carries the field name + the static
 * `URGENCY_PATTERN_CLASSES` label only. The matched substring NEVER
 * reaches the card (the handler at `r08_no_urgency.ts:30-31` documents
 * the same property byte-for-byte; ADR-0123 §3.1 note 1).
 */
export interface EvidenceCardR08Urgency {
  readonly kind: 'r08_urgency';
  readonly field: 'action' | 'merchant' | 'sku';
  readonly pattern_class: string;
  readonly pattern_id: string;
}

/**
 * r09 — drip pricing (cart_total != quoted_total). Comparator of
 * `intent.quoted_total {currency}` vs `cart_total {currency}` (both
 * numeric, both signed).
 */
export interface EvidenceCardR09DripPricing {
  readonly kind: 'r09_drip_pricing';
  readonly field: 'quoted_total';
  readonly comparator: { left: string; right: string };
}

/**
 * r14 — cart_total above intent.max_amount ceiling. Comparator of
 * `cart_total {currency}` vs `cap intent.max_amount {currency}`. The
 * cart_total side is the protocol_context value (signed by the agent);
 * cap is the user-signed intent ceiling.
 */
export interface EvidenceCardR14CartOverIntent {
  readonly kind: 'r14_cart_over_intent';
  readonly field: 'max_amount';
  readonly comparator: { left: string; right: string };
}

/**
 * r15 — cart_currency != intent.currency. Comparator of two ISO-4217
 * strings, both pinned to the bounded enum-shaped surface (never
 * attacker-controllable narrative).
 */
export interface EvidenceCardR15CurrencyMismatch {
  readonly kind: 'r15_currency_mismatch';
  readonly field: 'currency';
  readonly comparator: { left: string; right: string };
}

/**
 * r18 — skill-manifest digest absent OR not in the merchant's
 * allow-list. Static headline; `approved_count` is the merchant's
 * configured allow-list size (never the digest itself — see
 * ADR-0123 §3.1 table for the safety property).
 */
export interface EvidenceCardR18Manifest {
  readonly kind: 'r18_manifest_category';
  readonly field: 'skill_manifest';
  readonly headline: string;
  readonly approved_count?: number;
}

/**
 * r19 — subscription-creating action without a user-signed mandate.
 * Static categorical sentence.
 */
export interface EvidenceCardR19Subscription {
  readonly kind: 'r19_subscription_category';
  readonly field: 'principal';
  readonly headline: string;
}

/**
 * r25 — canonical body bytes over the configured cap. The handler
 * does NOT carry the observed size on the reason (`r25_size_limit.ts:53`
 * — the reason is `payload_too_large` only); intent_summary carries no
 * `signed_body_bytes` either. The composer therefore renders cap-only
 * ("exceeded · {N} bytes cap"). See ADR-0123 §3.1 table r25 row.
 */
export interface EvidenceCardR25BodyBytes {
  readonly kind: 'r25_over_body_bytes';
  readonly field: 'signed_body';
  readonly comparator: { left: string; right: string };
}

/**
 * r_custom — merchant-authored DSL expression failed. The expression
 * is MERCHANT-AUTHORED (`rule.params.expression`), not
 * attacker-controllable — but still rendered as a plain React text
 * node + capped at 80 chars (mirrors `why-this-rule.ts:263` byte-for-
 * byte) per ADR-0123 §4 must-hold 6.
 */
export interface EvidenceCardRCustomPredicate {
  readonly kind: 'r_custom_predicate';
  readonly field: 'predicate';
  readonly expression_display: string;
}

/**
 * Generic always-a-card fallback (ADR-0123 §2.1(c)). Returned for ANY
 * row whose `fail_reason` is null / unmapped / outside the per-kind
 * builders. Carries the categorical `whyThisRule` sentence (which
 * itself never reads the mandate — `why-this-rule.ts:14-21`) and the
 * decision verb. For the indeterminate r03 / r04 / r13 / r17 set the
 * `indeterminate_note` carries the existing `INDETERMINATE_SENTENCE`
 * verbatim so the drawer's honesty disclosure is byte-identical to
 * the row's (ADR-0117 / ADR-0120 §3.4 honesty preserved).
 */
export interface EvidenceCardGeneric {
  readonly kind: 'generic';
  readonly field: 'rule';
  readonly headline: string;
  readonly indeterminate_note?: string;
  readonly decision: 'reject' | 'escalate';
}

/**
 * Optional caller-supplied surface for r23. The composer takes this
 * separately from `AuditIntentSummary` because `intent_summary` does
 * not itself carry `issued_at` / `expires_at` (those live on the
 * canonical mandate payload). The caller derives the requested
 * validity seconds from `record.payload?.request?.canonical_json`
 * (when present); the composer formats and renders whichever is
 * available.
 *
 * ADR-0123 also threads `protocol_context.cart_total` /
 * `cart_currency` through this surface for the r09 / r14 / r15
 * comparators. Both fields are optional — the composer renders the
 * comparator with a "—" left side when missing.
 */
export interface EvidenceCardContext {
  readonly intent_summary: AuditIntentSummary | undefined;
  /** r23 — caller-derived validity window in seconds. Absent when the
   *  payload was not surfaced; the comparator still renders the cap. */
  readonly requested_validity_seconds?: number;
  /** r09 / r14 — caller-derived cart_total from the mandate payload's
   *  `signed.protocol_context.cart_total`. Absent when the payload was
   *  not surfaced. */
  readonly cart_total?: number;
  /** r15 — caller-derived cart_currency from the mandate payload's
   *  `signed.protocol_context.cart_currency`. Absent when the payload
   *  was not surfaced. */
  readonly cart_currency?: string;
}

/**
 * Fallback context — the always-a-card branch needs the rule_id (to
 * compose the `whyThisRule` sentence + check indeterminate honesty),
 * the merchant's `rules` array (`whyThisRule` looks up the configured
 * rule by id), and the decision verb (so the renderer can color-code
 * the chrome — coral for reject, amber for escalate).
 */
export interface EvidenceCardFallback {
  readonly rule_id: string;
  readonly rules: readonly Rule[];
  readonly decision: 'reject' | 'escalate';
}

/**
 * Static pattern-class label tables, colocated with the composer.
 * KEEP THESE IN SYNC with `packages/policy/src/content_patterns.ts`
 * (`SECRET_PATTERNS` + `INJECTION_PATTERNS`) and with
 * `packages/policy/src/rules/r08_no_urgency.ts` (`URGENCY_PATTERNS`).
 * The keys are the `name` field on each pattern; the values are the
 * human-readable label the operator sees.
 *
 * NEVER add a pattern_id whose label includes mandate bytes; the
 * labels here are static + categorical by construction.
 */
const SECRET_PATTERN_CLASSES: Readonly<Record<string, string>> = Object.freeze({
  aws_access_key: 'AWS access-key shape',
  github_pat: 'GitHub personal-access-token shape',
  stripe_key: 'Stripe API-key shape',
  jwt: 'JWT shape',
  bearer_token: 'Bearer-token header shape',
});

const INJECTION_PATTERN_CLASSES: Readonly<Record<string, string>> = Object.freeze({
  model_token_marker: 'model-control marker',
  llama_instruction_tag: 'Llama-style instruction tag',
  ignore_previous_directive: 'explicit-override phrasing',
  dan_jailbreak: 'DAN-jailbreak phrasing',
});

/**
 * ADR-0123 §3.1 note 1 — categorical labels for the r08 urgency
 * pattern set. Keys MUST match the `name` field on each entry in
 * `packages/policy/src/rules/r08_no_urgency.ts`'s `URGENCY_PATTERNS`
 * (verified 2026-06-17). The label is the only thing the card ever
 * surfaces; the matched substring is NEVER carried.
 */
const URGENCY_PATTERN_CLASSES: Readonly<Record<string, string>> = Object.freeze({
  limited_time: "artificial-scarcity 'limited time' phrasing",
  only_n_left: 'false-scarcity counter',
  act_now: "pressure imperative 'act now'",
  hurry: "pressure imperative 'hurry'",
  last_chance: "pressure imperative 'last chance'",
  ending_soon: "countdown framing 'ending soon'",
  expires_soon_or_in: "countdown framing 'expires soon/in'",
  countdown: 'countdown-clock phrasing',
});

/**
 * Fixed safe-prefix length per credential pattern class. The composer
 * keeps AT MOST these many leading characters from the offending field
 * value; everything else is replaced by the fixed mask. The values
 * here are deliberately small so that even a partial leak is
 * categorical, not byte-revealing:
 *
 *   - AWS access keys start with the literal `AKIA` (4 chars), which
 *     is the shape itself — keeping it preserves the categorical
 *     signal without exposing the random suffix.
 *   - GitHub PATs / Stripe keys / Bearer tokens carry a categorical
 *     prefix (`ghp_` / `sk_live_` / `Bearer `) that uniquely
 *     identifies the class; we keep up to 4 chars.
 *   - JWTs always start with `eyJ` (b64-encoded `{"`); 3 chars.
 *
 * The mapping is closed: any future pattern needs an entry here AND
 * an entry in `SECRET_PATTERN_CLASSES`. An unknown pattern_id falls
 * back to 0 (no prefix), which is the most conservative posture.
 */
const SECRET_SAFE_PREFIX_LEN: Readonly<Record<string, number>> = Object.freeze({
  aws_access_key: 4,
  github_pat: 4,
  stripe_key: 4,
  jwt: 3,
  bearer_token: 4,
});

/** Fixed visual mask. Length is deliberately constant so the masked
 *  display never leaks the source value's length. */
const SECRET_MASK = '█'.repeat(16); // ████████████████

/**
 * Unicode tag-block range (mirrors `packages/policy/src/content_patterns.ts`
 * `TAG_BLOCK_START` / `TAG_BLOCK_END`). Duplicated locally so this
 * module can stay a leaf dependency (no `@sill/policy` import).
 */
const TAG_BLOCK_START = 0xe0000;
const TAG_BLOCK_END = 0xe007f;

/** ADR-0123 §4 must-hold 6 — r_custom expression display cap. Mirrors
 *  `why-this-rule.ts:263` byte-for-byte. */
const R_CUSTOM_EXPRESSION_MAX_CHARS = 80;

/**
 * Visualize invisible / format / zero-width codepoints in a string.
 *
 * Every codepoint in the Unicode tag-block range (U+E0000–U+E007F)
 * AND a small set of other commonly-abused invisible classes
 * (zero-width joiner / non-joiner, format characters, BOM, soft
 * hyphen) is replaced by a visible sentinel of the shape
 * `␛⟨U+XXXX⟩` (`␛` is the ECMA "ESC" visible control picture).
 * The raw invisible codepoint is NEVER carried over to the output
 * string.
 *
 * Returns the sanitized string + the per-codepoint annotations + the
 * count of invisible codepoints found. The caller uses the count to
 * render a badge ("N invisible tag-block characters") and the
 * annotations to render a small breakdown table.
 *
 * Iterates by codepoint (the `for…of` over a string yields code-point
 * grapheme clusters, mirroring `r20_unicode_tag_block.ts:33`).
 */
export function visualizeInvisible(
  raw: string,
): {
  readonly visualized: string;
  readonly codepoints: ReadonlyArray<{ index: number; codepoint: string }>;
  readonly invisible_count: number;
} {
  const out: string[] = [];
  const codepoints: { index: number; codepoint: string }[] = [];
  let index = 0;
  for (const ch of raw) {
    const cp = ch.codePointAt(0);
    if (cp === undefined) {
      out.push(ch);
      index += 1;
      continue;
    }
    if (isInvisibleCodepoint(cp)) {
      const hex = cp.toString(16).toUpperCase().padStart(4, '0');
      const annot = `U+${hex}`;
      // The visible sentinel — `␛` (ESC control picture) + the codepoint
      // annotation. The raw invisible char `ch` is NEVER pushed.
      out.push('␛⟨' + annot + '⟩');
      codepoints.push({ index, codepoint: annot });
    } else {
      out.push(ch);
    }
    index += 1;
  }
  return {
    visualized: out.join(''),
    codepoints,
    invisible_count: codepoints.length,
  };
}

/**
 * Closed set of "invisible" codepoints the visualizer escapes. The
 * tag-block range is the r20 vector; the others are commonly-abused
 * classes (zero-width characters, format controls, BOM, soft hyphen)
 * that ALSO render as nothing in browsers and would otherwise smuggle
 * an attacker payload past a text-node render. Defense in depth — r20
 * only fires on tag-block hits, but a future rule may extend the set
 * and the visualizer must already cover it.
 */
function isInvisibleCodepoint(cp: number): boolean {
  if (cp >= TAG_BLOCK_START && cp <= TAG_BLOCK_END) return true;
  // Zero-width characters
  if (cp === 0x200b || cp === 0x200c || cp === 0x200d) return true;
  // BiDi / format controls
  if (cp === 0x200e || cp === 0x200f) return true;
  if (cp >= 0x202a && cp <= 0x202e) return true;
  if (cp >= 0x2066 && cp <= 0x2069) return true;
  // Word joiner / BOM / interlinear annotation
  if (cp === 0x2060 || cp === 0xfeff) return true;
  if (cp === 0xfff9 || cp === 0xfffa || cp === 0xfffb) return true;
  // Soft hyphen
  if (cp === 0x00ad) return true;
  return false;
}

/**
 * Mask a credential value to its categorical safe prefix + fixed
 * mask. The composer NEVER returns the raw secret beyond the
 * safe-prefix; the prefix length is bounded by the pattern class's
 * entry in `SECRET_SAFE_PREFIX_LEN` (defaulting to 0 for unknown
 * classes — fail-closed).
 */
function maskCredential(raw: string, pattern_id: string): string {
  const prefix_len = SECRET_SAFE_PREFIX_LEN[pattern_id] ?? 0;
  // Take up to `prefix_len` leading chars. We use `.slice` on the JS
  // string (UTF-16 code units); credentials are pure ASCII by
  // pattern so this is equivalent to a codepoint slice and avoids
  // an Array.from allocation per row.
  const safe_prefix = raw.slice(0, prefix_len);
  return safe_prefix + SECRET_MASK;
}

/**
 * The prefix vocabulary entries this dashboard version can render
 * (ADR-0122 §3.1 + ADR-0123 §3.2 — defense against a future
 * `fail_reason` prefix landing without dashboard support; an
 * unsupported prefix DOES NOT block a card — the composer falls
 * through to the GENERIC fallback per ADR-0123 §2.1(c)).
 */
export const EVIDENCE_CARD_SUPPORTED: ReadonlySet<string> = new Set([
  // ADR-0122
  'secret_in_',
  'unicode_tag_in_',
  'injection_in_',
  'over_validity',
  'over_amount',
  'currency_uncapped',
  // ADR-0123
  'urgency_in_',
  'drip_pricing',
  'over_cart_ceiling',
  'currency_mismatch',
  'over_body_bytes',
  'principal_not_user_signed',
  'manifest_absent',
  'manifest_not_allowlisted',
  'subscription_no_consent',
  'over_destructive_ceiling',
  'destructive_currency_uncapped',
  'destructive_non_usd_unevaluable',
  'custom_predicate',
]);

/**
 * Compose an evidence card from the categorical `fail_reason` Slice 1
 * lands on `ShadowDecision` + the audit record's `intent_summary` +
 * the rule's params + the (`rule_id`, `rules`, `decision`) fallback
 * context. ADR-0123 GUARANTEE — this function is TOTAL: it NEVER
 * returns `null`.
 *
 * Dispatch order:
 *   1. null / empty `fail_reason` ⇒ generic fallback.
 *   2. a known prefix ⇒ per-kind builder; if the builder returns
 *      null (defensive — stale projection, missing intent field,
 *      unknown pattern_id) the function falls through to the
 *      generic fallback.
 *   3. unknown prefix ⇒ generic fallback.
 *
 * SECURITY (carried forward from ADR-0122 §4, restated in ADR-0123 §4):
 *   - masking + escaping happen HERE, not in the renderer;
 *   - NEVER reads `intent_summary` keys that are not in the
 *     `EvidenceCardField` set (defense against a future projection
 *     adding a PII field);
 *   - regex-free at runtime — uses startsWith + indexOf to parse the
 *     fail_reason; no string interpolation of mandate bytes outside
 *     the masked / visualized branches;
 *   - r08 urgency carries the pattern-class label only — NEVER the
 *     matched substring (ADR-0123 §3.1 note 1);
 *   - r_custom expression is capped at 80 chars + plain text node
 *     (ADR-0123 §4 must-hold 6).
 */
export function buildEvidenceCard(
  fail_reason: string | null | undefined,
  ctx: EvidenceCardContext,
  rule_params: Rule['params'] | undefined,
  fallback: EvidenceCardFallback,
): EvidenceCard {
  const specific = tryBuildSpecificCard(fail_reason, ctx, rule_params);
  if (specific !== null) return specific;
  return buildGenericCard(fallback);
}

/**
 * Per-kind dispatch. Returns `null` on no-card-applicable so the
 * total `buildEvidenceCard` can fall through to the generic
 * fallback. NEVER reads outside the closed-set field surface.
 */
function tryBuildSpecificCard(
  fail_reason: string | null | undefined,
  ctx: EvidenceCardContext,
  rule_params: Rule['params'] | undefined,
): EvidenceCard | null {
  if (fail_reason === null || fail_reason === undefined || fail_reason === '') {
    return null;
  }
  // Closed-set prefix dispatch. The order is significant — the
  // suffix-bearing prefixes (`secret_in_`, `unicode_tag_in_`,
  // `injection_in_`, `urgency_in_`) MUST be tested before the bare
  // ones because `over_amount` is a prefix of `over_amount_` etc.
  if (fail_reason.startsWith('secret_in_')) {
    return buildCredentialCard(fail_reason, ctx.intent_summary);
  }
  if (fail_reason.startsWith('unicode_tag_in_')) {
    return buildUnicodeCard(fail_reason, ctx.intent_summary);
  }
  if (fail_reason.startsWith('injection_in_')) {
    return buildInjectionCard(fail_reason);
  }
  if (fail_reason.startsWith('urgency_in_')) {
    return buildUrgencyCard(fail_reason);
  }
  if (fail_reason === 'over_validity' || fail_reason.startsWith('over_validity')) {
    return buildValidityCard(ctx.requested_validity_seconds, rule_params);
  }
  if (fail_reason === 'over_amount' || fail_reason.startsWith('over_amount')) {
    return buildAmountCard(ctx.intent_summary, rule_params);
  }
  if (
    fail_reason === 'currency_uncapped'
    || fail_reason.startsWith('currency_uncapped')
  ) {
    return buildCurrencyUncappedCard(ctx.intent_summary);
  }
  // -- ADR-0123 bare-prefix dispatch --
  if (
    fail_reason === 'principal_not_user_signed'
    || fail_reason.startsWith('principal_not_user_signed')
  ) {
    return buildR02PrincipalCard();
  }
  if (
    fail_reason === 'over_destructive_ceiling'
    || fail_reason.startsWith('over_destructive_ceiling')
  ) {
    return buildR07DestructiveAmountCard(ctx.intent_summary, rule_params);
  }
  if (
    fail_reason === 'destructive_currency_uncapped'
    || fail_reason.startsWith('destructive_currency_uncapped')
  ) {
    return buildR07DestructiveCurrencyUncappedCard(ctx.intent_summary, false);
  }
  if (
    fail_reason === 'destructive_non_usd_unevaluable'
    || fail_reason.startsWith('destructive_non_usd_unevaluable')
  ) {
    return buildR07DestructiveCurrencyUncappedCard(ctx.intent_summary, true);
  }
  if (fail_reason === 'drip_pricing' || fail_reason.startsWith('drip_pricing')) {
    return buildR09DripPricingCard(ctx.intent_summary, ctx.cart_total);
  }
  if (
    fail_reason === 'over_cart_ceiling'
    || fail_reason.startsWith('over_cart_ceiling')
  ) {
    return buildR14CartOverIntentCard(ctx.intent_summary, ctx.cart_total);
  }
  if (
    fail_reason === 'currency_mismatch'
    || fail_reason.startsWith('currency_mismatch')
  ) {
    return buildR15CurrencyMismatchCard(ctx.intent_summary, ctx.cart_currency);
  }
  if (
    fail_reason === 'over_body_bytes'
    || fail_reason.startsWith('over_body_bytes')
  ) {
    return buildR25BodyBytesCard(rule_params);
  }
  if (fail_reason === 'manifest_absent' || fail_reason.startsWith('manifest_absent')) {
    return buildR18ManifestCard('absent', rule_params);
  }
  if (
    fail_reason === 'manifest_not_allowlisted'
    || fail_reason.startsWith('manifest_not_allowlisted')
  ) {
    return buildR18ManifestCard('mismatch', rule_params);
  }
  if (
    fail_reason === 'subscription_no_consent'
    || fail_reason.startsWith('subscription_no_consent')
  ) {
    return buildR19SubscriptionCard();
  }
  if (
    fail_reason === 'custom_predicate'
    || fail_reason.startsWith('custom_predicate')
  ) {
    return buildRCustomPredicateCard(rule_params);
  }
  // Unknown prefix — let the caller fall through to the generic card.
  return null;
}

/**
 * Generic always-a-card fallback. ADR-0123 §2.1(c) — composes the
 * categorical `whyThisRule` sentence + the decision verb + (for
 * indeterminate rules only) the verbatim `INDETERMINATE_SENTENCE`.
 *
 * The `whyThisRule` helper is itself categorical by construction
 * (`why-this-rule.ts:14-21` documents the property byte-for-byte);
 * the merchant's own `rule.params.expression` is the only narrative-
 * shaped string this branch can introduce, and that string is
 * capped at 80 chars inside `whyThisRule` already
 * (`why-this-rule.ts:263`).
 */
function buildGenericCard(fallback: EvidenceCardFallback): EvidenceCardGeneric {
  const headline = whyThisRule(fallback.rule_id, fallback.rules);
  const note = isIndeterminateShadowRule(fallback.rule_id as RuleId)
    ? INDETERMINATE_SENTENCE
    : undefined;
  return {
    kind: 'generic',
    field: 'rule',
    headline,
    decision: fallback.decision,
    ...(note !== undefined ? { indeterminate_note: note } : {}),
  };
}

/**
 * `secret_in_${field}_${pattern_id}` — split on the field+pattern
 * boundary. The handler emits `secret_in_<field>_<pattern_id>` where
 * `<field>` is one of `action` / `merchant` / `sku` and
 * `<pattern_id>` is one of the SECRET_PATTERNS names.
 */
function buildCredentialCard(
  fail_reason: string,
  intent: AuditIntentSummary | undefined,
): EvidenceCardCredential | null {
  const tail = fail_reason.slice('secret_in_'.length);
  const parsed = parseFieldAndPattern(tail);
  if (parsed === null) return null;
  const { field, pattern_id } = parsed;
  if (intent === undefined) return null;
  const raw = readIntentField(intent, field);
  if (raw === undefined) return null;
  const pattern_class = SECRET_PATTERN_CLASSES[pattern_id];
  if (pattern_class === undefined) return null;
  // Mask at composition time. The raw secret is NEVER carried beyond
  // `maskCredential`'s safe-prefix; the returned card carries only
  // the masked display.
  const masked_display = maskCredential(raw, pattern_id);
  return {
    kind: 'r22_credential',
    field,
    pattern_class,
    pattern_id,
    masked_display,
  };
}

/**
 * `unicode_tag_in_${field}` — bare field suffix. The handler emits
 * the field name directly with no pattern id (the r20 rule has a
 * single "is this codepoint in the tag-block range" predicate).
 */
function buildUnicodeCard(
  fail_reason: string,
  intent: AuditIntentSummary | undefined,
): EvidenceCardUnicode | null {
  const tail = fail_reason.slice('unicode_tag_in_'.length);
  if (!isFieldName(tail)) return null;
  if (intent === undefined) return null;
  const raw = readIntentField(intent, tail);
  if (raw === undefined) return null;
  const { visualized, codepoints, invisible_count } = visualizeInvisible(raw);
  // Defensive: if the value contains zero invisible codepoints, the
  // rule wouldn't have fired — but a stale projection / mismatched
  // fail_reason could still land us here. Return null rather than
  // render an empty card.
  if (invisible_count === 0) return null;
  return {
    kind: 'r20_unicode',
    field: tail,
    visualized,
    invisible_count,
    codepoints,
  };
}

/**
 * `injection_in_${field}_${pattern_id}` — split on the field+pattern
 * boundary. NEVER reads or renders the matched bytes; only the
 * pattern class label.
 */
function buildInjectionCard(
  fail_reason: string,
): EvidenceCardInjection | null {
  const tail = fail_reason.slice('injection_in_'.length);
  const parsed = parseFieldAndPattern(tail);
  if (parsed === null) return null;
  const { field, pattern_id } = parsed;
  const pattern_class = INJECTION_PATTERN_CLASSES[pattern_id];
  if (pattern_class === undefined) return null;
  return {
    kind: 'r10_injection',
    field,
    pattern_class,
    pattern_id,
  };
}

/**
 * `urgency_in_${field}_${pattern_id}` — same shape as the injection
 * card. The pattern_id is one of the static `URGENCY_PATTERNS` names
 * in `r08_no_urgency.ts:44-77`; the matched substring NEVER reaches
 * here (the handler does not carry it on the reason — see
 * `r08_no_urgency.ts:30-31` + ADR-0123 §3.1 note 1).
 *
 * The `urgency_in_` prefix is followed by a multi-underscore field +
 * pattern_id (e.g. `urgency_in_sku_only_n_left`). `parseFieldAndPattern`
 * splits on the FIRST underscore after the known field name, so a
 * multi-token pattern_id (`only_n_left`, `expires_soon_or_in`, etc.)
 * stays intact as the pattern_id.
 */
function buildUrgencyCard(
  fail_reason: string,
): EvidenceCardR08Urgency | null {
  const tail = fail_reason.slice('urgency_in_'.length);
  const parsed = parseFieldAndPattern(tail);
  if (parsed === null) return null;
  const { field, pattern_id } = parsed;
  const pattern_class = URGENCY_PATTERN_CLASSES[pattern_id];
  if (pattern_class === undefined) return null;
  return {
    kind: 'r08_urgency',
    field,
    pattern_class,
    pattern_id,
  };
}

/**
 * `over_validity` — r23. Requested seconds may be unavailable (the
 * caller is responsible for deriving them from the canonical mandate
 * payload). The comparator still renders the cap.
 */
function buildValidityCard(
  requested_seconds: number | undefined,
  params: Rule['params'] | undefined,
): EvidenceCardValidity | null {
  const cap = readR23Cap(params);
  if (cap === undefined) return null;
  const left =
    requested_seconds !== undefined && Number.isFinite(requested_seconds)
      ? `${requested_seconds}s requested`
      : '— requested';
  const right = `${cap}s cap`;
  return {
    kind: 'r23_validity',
    field: 'expires_at',
    comparator: { left, right },
  };
}

/**
 * `over_amount` — r05 (mapped from `amount_exceeds_cap` by the edge).
 * Reads `intent.max_amount` + `intent.currency` (the agent-signed
 * numbers — categorical to display, not attacker-controllable as
 * narrative text); reads the per-currency cap from `rule.params`.
 */
function buildAmountCard(
  intent: AuditIntentSummary | undefined,
  params: Rule['params'] | undefined,
): EvidenceCardAmount | null {
  if (intent === undefined) return null;
  const currency = intent.currency;
  if (typeof currency !== 'string' || currency === '') return null;
  // Effective amount is `quoted_total` when present, else `max_amount`
  // (mirrors `r05_max_amount.ts:113` so the comparator shows the same
  // number the rule evaluated).
  const effective = typeof intent.quoted_total === 'number'
    ? intent.quoted_total
    : intent.max_amount;
  if (typeof effective !== 'number' || !Number.isFinite(effective)) return null;
  const cap = readR05Cap(params, currency);
  if (cap === undefined) return null;
  const left = `${formatAmount(effective)} ${currency}`;
  const right = `cap ${formatAmount(cap)} ${currency}`;
  return {
    kind: 'r05_amount',
    field: 'max_amount',
    comparator: { left, right },
  };
}

/**
 * `currency_uncapped` — r05 with the merchant's `on_unlisted_currency:
 * 'reject'` posture. Categorical sentence ("no cap configured for
 * USD") with the ISO-4217 string from the intent — the currency is a
 * bounded enum-shaped value, not free-form narrative.
 */
function buildCurrencyUncappedCard(
  intent: AuditIntentSummary | undefined,
): EvidenceCardCurrencyUncapped | null {
  if (intent === undefined) return null;
  const currency = intent.currency;
  if (typeof currency !== 'string' || currency === '') return null;
  return {
    kind: 'r05_currency_uncapped',
    field: 'max_amount',
    headline: `no cap configured for ${currency}`,
  };
}

/* ------------------------------------------------------------------ */
/* ADR-0123 §3.3 per-kind builders                                    */
/* ------------------------------------------------------------------ */

/**
 * r02 — `principal_not_user_signed`. Static categorical sentence; never
 * reads the mandate.
 */
function buildR02PrincipalCard(): EvidenceCardR02Principal {
  return {
    kind: 'r02_principal_category',
    field: 'principal',
    headline: 'principal is not user-delegated (signed)',
  };
}

/**
 * r07 — `over_destructive_ceiling`. Comparator of `intent.max_amount
 * {currency}` vs `cap {ceiling} {currency}` (cap from
 * `rule.params.auto_approve_caps[currency]` or the legacy
 * `max_auto_approve_usd`). Returns null when intent currency or cap
 * isn't recoverable — the caller falls through to the generic card.
 */
function buildR07DestructiveAmountCard(
  intent: AuditIntentSummary | undefined,
  params: Rule['params'] | undefined,
): EvidenceCardR07DestructiveAmount | null {
  if (intent === undefined) return null;
  const currency = intent.currency;
  if (typeof currency !== 'string' || currency === '') return null;
  const amount = typeof intent.max_amount === 'number'
    && Number.isFinite(intent.max_amount)
    ? intent.max_amount
    : undefined;
  if (amount === undefined) return null;
  const cap = readR07Cap(params, currency);
  if (cap === undefined) return null;
  const left = `${formatAmount(amount)} ${currency}`;
  const right = `cap ${formatAmount(cap)} ${currency}`;
  return {
    kind: 'r07_destructive_amount',
    field: 'max_amount',
    comparator: { left, right },
  };
}

/**
 * r07 — `destructive_currency_uncapped` AND `destructive_non_usd_unevaluable`.
 * Static headline; `legacy=true` carries the legacy USD-only annotation.
 * Returns null only when the intent currency is missing.
 */
function buildR07DestructiveCurrencyUncappedCard(
  intent: AuditIntentSummary | undefined,
  legacy: boolean,
): EvidenceCardR07DestructiveCurrencyUncapped | null {
  if (intent === undefined) return null;
  const currency = intent.currency;
  if (typeof currency !== 'string' || currency === '') return null;
  const headline = legacy
    ? `no auto-approve ceiling for ${currency} (legacy USD-only config)`
    : `no auto-approve ceiling configured for ${currency}`;
  return {
    kind: 'r07_destructive_currency_uncapped',
    field: 'max_amount',
    headline,
    legacy,
  };
}

/**
 * r09 — drip pricing. Comparator of `intent.quoted_total {currency}`
 * vs `cart_total {currency}`. The cart_total is the protocol_context
 * value the caller derived from the payload; renders "—" when
 * unavailable. Returns null when neither side is available — the
 * caller falls through to the generic card.
 */
function buildR09DripPricingCard(
  intent: AuditIntentSummary | undefined,
  cart_total: number | undefined,
): EvidenceCardR09DripPricing | null {
  if (intent === undefined) return null;
  const currency = intent.currency;
  if (typeof currency !== 'string' || currency === '') return null;
  const quoted = typeof intent.quoted_total === 'number'
    && Number.isFinite(intent.quoted_total)
    ? intent.quoted_total
    : undefined;
  const cart = typeof cart_total === 'number' && Number.isFinite(cart_total)
    ? cart_total
    : undefined;
  // If neither side is recoverable there's nothing useful to render —
  // fall through to the generic card.
  if (quoted === undefined && cart === undefined) return null;
  const left = quoted !== undefined
    ? `${formatAmount(quoted)} ${currency} quoted`
    : `— quoted`;
  const right = cart !== undefined
    ? `${formatAmount(cart)} ${currency} cart`
    : `— cart`;
  return {
    kind: 'r09_drip_pricing',
    field: 'quoted_total',
    comparator: { left, right },
  };
}

/**
 * r14 — `over_cart_ceiling`. Comparator of `cart_total {currency}` vs
 * `cap intent.max_amount {currency}`. The cart_total side is the
 * protocol_context value the caller derived from the payload; renders
 * "—" when unavailable. Returns null when intent.max_amount or
 * intent.currency is missing — the caller falls through to the
 * generic card.
 */
function buildR14CartOverIntentCard(
  intent: AuditIntentSummary | undefined,
  cart_total: number | undefined,
): EvidenceCardR14CartOverIntent | null {
  if (intent === undefined) return null;
  const currency = intent.currency;
  if (typeof currency !== 'string' || currency === '') return null;
  const ceiling = typeof intent.max_amount === 'number'
    && Number.isFinite(intent.max_amount)
    ? intent.max_amount
    : undefined;
  if (ceiling === undefined) return null;
  const left = typeof cart_total === 'number' && Number.isFinite(cart_total)
    ? `${formatAmount(cart_total)} ${currency} cart`
    : `— cart`;
  const right = `cap ${formatAmount(ceiling)} ${currency} intent`;
  return {
    kind: 'r14_cart_over_intent',
    field: 'max_amount',
    comparator: { left, right },
  };
}

/**
 * r15 — `currency_mismatch`. Comparator of two ISO-4217 codes
 * (intent.currency vs cart_currency). The cart_currency side comes
 * from the caller (mandate payload); "—" when unavailable. Returns
 * null when intent.currency is missing — the caller falls through to
 * the generic card.
 */
function buildR15CurrencyMismatchCard(
  intent: AuditIntentSummary | undefined,
  cart_currency: string | undefined,
): EvidenceCardR15CurrencyMismatch | null {
  if (intent === undefined) return null;
  const currency = intent.currency;
  if (typeof currency !== 'string' || currency === '') return null;
  // Defensive: pin both sides to an ISO-4217-shaped string (uppercase
  // alphanumeric, length 3). Anything else collapses to "—" so an
  // attacker-stuffed cart_currency cannot become a narrative-shaped
  // text node. The intent.currency is the agent-signed ISO-4217 value
  // (categorical by mandate-bounds — same posture as the r05 currency
  // surface).
  const left = `${currency} intent`;
  const right = isIso4217Shape(cart_currency)
    ? `${cart_currency} cart`
    : `— cart`;
  return {
    kind: 'r15_currency_mismatch',
    field: 'currency',
    comparator: { left, right },
  };
}

/**
 * r25 — `over_body_bytes`. Cap-only comparator: the handler does NOT
 * carry the observed size on the reason and `intent_summary` carries
 * no `signed_body_bytes` either (ADR-0123 §3.1 r25 row). Display is
 * `exceeded · {N} bytes cap`. Returns null when the cap is missing —
 * the caller falls through to the generic card.
 */
function buildR25BodyBytesCard(
  params: Rule['params'] | undefined,
): EvidenceCardR25BodyBytes | null {
  const cap = readR25Cap(params);
  if (cap === undefined) return null;
  const left = 'exceeded';
  const right = `${cap.toLocaleString('en-US')} bytes cap`;
  return {
    kind: 'r25_over_body_bytes',
    field: 'signed_body',
    comparator: { left, right },
  };
}

/**
 * r18 — `manifest_absent` AND `manifest_not_allowlisted`. Static
 * headline; `approved_count` is the merchant's configured allow-list
 * size (never the digest itself).
 */
function buildR18ManifestCard(
  kind: 'absent' | 'mismatch',
  params: Rule['params'] | undefined,
): EvidenceCardR18Manifest {
  const approved_count = readR18ApprovedCount(params);
  if (kind === 'absent') {
    return {
      kind: 'r18_manifest_category',
      field: 'skill_manifest',
      headline: 'agent did not attest a skill-manifest digest',
      ...(approved_count !== undefined ? { approved_count } : {}),
    };
  }
  const n = approved_count ?? 0;
  const noun = n === 1 ? 'entry' : 'entries';
  return {
    kind: 'r18_manifest_category',
    field: 'skill_manifest',
    headline: `attested manifest digest is not in your approved allow-list (${n} ${noun})`,
    ...(approved_count !== undefined ? { approved_count } : {}),
  };
}

/**
 * r19 — `subscription_no_consent`. Static categorical sentence; never
 * reads the mandate.
 */
function buildR19SubscriptionCard(): EvidenceCardR19Subscription {
  return {
    kind: 'r19_subscription_category',
    field: 'principal',
    headline: 'subscription-creating action without a user-signed mandate',
  };
}

/**
 * r_custom — `custom_predicate`. Carries the merchant-authored
 * expression capped at 80 chars (mirrors `why-this-rule.ts:263`
 * byte-for-byte). The merchant authored the expression — it is not
 * attacker-controllable — but the cap + plain-text rendering still
 * enforce a bounded display surface per ADR-0123 §4 must-hold 6.
 */
function buildRCustomPredicateCard(
  params: Rule['params'] | undefined,
): EvidenceCardRCustomPredicate | null {
  if (params === undefined) return null;
  if (params.type !== 'r_custom') return null;
  const expr = (params as { expression?: string }).expression;
  if (typeof expr !== 'string' || expr.length === 0) return null;
  const trimmed = expr.length > R_CUSTOM_EXPRESSION_MAX_CHARS
    ? `${expr.slice(0, R_CUSTOM_EXPRESSION_MAX_CHARS - 3)}…`
    : expr;
  return {
    kind: 'r_custom_predicate',
    field: 'predicate',
    expression_display: trimmed,
  };
}

/* ------------------------------------------------------------------ */
/* Parsing + reading helpers — closed sets, no regex.                 */
/* ------------------------------------------------------------------ */

type CardableTextField = 'action' | 'merchant' | 'sku';

function isFieldName(s: string): s is CardableTextField {
  return s === 'action' || s === 'merchant' || s === 'sku';
}

/**
 * ISO-4217 shape predicate. Bounded to 3 uppercase-ASCII letters.
 * Anything else is treated as missing — defense-in-depth so an
 * attacker-stuffed cart_currency cannot become a narrative text node
 * (the verifier already enforces a much stronger ISO-4217 allow-list,
 * but the composer is the last gate on the render surface).
 */
function isIso4217Shape(s: string | undefined): s is string {
  if (typeof s !== 'string') return false;
  if (s.length !== 3) return false;
  for (let i = 0; i < 3; i += 1) {
    const c = s.charCodeAt(i);
    if (c < 0x41 || c > 0x5a) return false;
  }
  return true;
}

/**
 * Parse a `${field}_${pattern_id}` suffix.
 *
 * The field names are the closed set `action` / `merchant` / `sku`;
 * the pattern ids are whatever the handler emits (and which then
 * appear in `SECRET_PATTERN_CLASSES` / `INJECTION_PATTERN_CLASSES` /
 * `URGENCY_PATTERN_CLASSES`). Strategy: check each known field as a
 * prefix; the pattern id is whatever follows the underscore. Multi-
 * token pattern ids (e.g. `only_n_left`, `expires_soon_or_in`) are
 * preserved verbatim because we split ONLY at the first underscore
 * after the known field name.
 */
function parseFieldAndPattern(
  suffix: string,
): { field: CardableTextField; pattern_id: string } | null {
  for (const f of ['action', 'merchant', 'sku'] as const) {
    if (suffix === f) {
      // Field with no pattern id — not expected for r22/r10/r08 but
      // defensive.
      return null;
    }
    if (suffix.startsWith(f + '_')) {
      const pattern_id = suffix.slice(f.length + 1);
      if (pattern_id === '') return null;
      return { field: f, pattern_id };
    }
  }
  return null;
}

/**
 * Read a `CardableTextField` off `intent_summary`. Returns the string
 * value when present and string-typed; `undefined` otherwise (the
 * composer's caller emits no card in that case).
 *
 * NEVER reads any other key. This is the load-bearing minimization
 * (`intent.merchant` is the only narrative-shaped field the composer
 * ever touches).
 */
function readIntentField(
  intent: AuditIntentSummary,
  field: CardableTextField,
): string | undefined {
  if (field === 'action') {
    return typeof intent.action === 'string' ? intent.action : undefined;
  }
  if (field === 'merchant') {
    return typeof intent.merchant === 'string' ? intent.merchant : undefined;
  }
  if (field === 'sku') {
    return typeof intent.sku === 'string' ? intent.sku : undefined;
  }
  return undefined;
}

/**
 * Read the r23 cap (`max_validity_seconds`) off the rule params.
 * Returns `undefined` when the params shape isn't r23 OR the cap is
 * missing / non-finite.
 */
function readR23Cap(params: Rule['params'] | undefined): number | undefined {
  if (params === undefined) return undefined;
  if (params.type !== 'r23') return undefined;
  const cap = params.max_validity_seconds;
  return typeof cap === 'number' && Number.isFinite(cap) ? cap : undefined;
}

/**
 * Read the r05 cap for the given currency. Honors both the new
 * `caps: Record<string, number>` shape AND the legacy
 * `max_amount_usd` scalar (which the handler treats as an implicit
 * USD cap when `caps` does not list it — mirrors
 * `r05_max_amount.ts:effectiveCaps()`).
 */
function readR05Cap(
  params: Rule['params'] | undefined,
  currency: string,
): number | undefined {
  if (params === undefined) return undefined;
  if (params.type !== 'r05') return undefined;
  const caps = params.caps;
  if (caps !== undefined && Object.prototype.hasOwnProperty.call(caps, currency)) {
    const v = caps[currency];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  if (currency === 'USD' && typeof params.max_amount_usd === 'number'
      && Number.isFinite(params.max_amount_usd)) {
    return params.max_amount_usd;
  }
  return undefined;
}

/**
 * Read the r07 destructive-action ceiling for the given currency.
 * Honors the new `auto_approve_caps: Record<string, number>` shape
 * AND the legacy `max_auto_approve_usd` scalar.
 */
function readR07Cap(
  params: Rule['params'] | undefined,
  currency: string,
): number | undefined {
  if (params === undefined) return undefined;
  if (params.type !== 'r07') return undefined;
  const p = params as {
    type: 'r07';
    max_auto_approve_usd?: number;
    auto_approve_caps?: Record<string, number>;
  };
  const caps = p.auto_approve_caps;
  if (caps !== undefined && Object.prototype.hasOwnProperty.call(caps, currency)) {
    const v = caps[currency];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  if (currency === 'USD' && typeof p.max_auto_approve_usd === 'number'
      && Number.isFinite(p.max_auto_approve_usd)) {
    return p.max_auto_approve_usd;
  }
  return undefined;
}

/**
 * Read the r25 `max_body_bytes` cap. Returns `undefined` when the
 * params shape isn't r25 OR the cap is missing / non-finite.
 */
function readR25Cap(params: Rule['params'] | undefined): number | undefined {
  if (params === undefined) return undefined;
  if (params.type !== 'r25') return undefined;
  const cap = (params as { max_body_bytes?: number }).max_body_bytes;
  return typeof cap === 'number' && Number.isFinite(cap) ? cap : undefined;
}

/**
 * Read the r18 approved-manifest-digest count. Never returns the
 * digests themselves — only their count.
 */
function readR18ApprovedCount(
  params: Rule['params'] | undefined,
): number | undefined {
  if (params === undefined) return undefined;
  if (params.type !== 'r18') return undefined;
  const hashes = (params as { manifest_hashes?: string[] }).manifest_hashes;
  if (!Array.isArray(hashes)) return undefined;
  return hashes.length;
}

/**
 * Locale-format a numeric amount. Two decimal places when the value
 * has a fractional part; integer when not. Conservative because the
 * comparator is informational (not a settlement number).
 */
function formatAmount(n: number): string {
  if (Number.isInteger(n)) {
    return n.toLocaleString('en-US');
  }
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
